<template>
    <div id="ActivtyTask">
        <div class="section-title">Hareketler</div>
        <div class="activity-container">
            <div v-if="activities === null" class="skeleton-list">
                <div v-for="i in 5" :key="i" class="skeleton-item">
                    <div class="skeleton-avatar"></div>
                    <div class="skeleton-body">
                        <div class="skeleton-line short"></div>
                        <div class="skeleton-line long"></div>
                    </div>
                </div>
            </div>
            <div v-else-if="activities && activities.length" class="activity-list">
                <div v-for="activity in activities" :key="activity._id" class="activity-item">
                    <div class="activity-left">
                        <div class="avatar-wrapper">
                            <Avatar
                                v-if="activity.actor === 'user'"
                                :size="32"
                                :src="usersMap[activity.actorUser] && usersMap[activity.actorUser].gravatar"
                                :username="usersMap[activity.actorUser] && usersMap[activity.actorUser].name"
                                v-tippy="{
                                    followCursor: true,
                                    placement: 'bottom-end',
                                }"
                                :content="usersMap[activity.actorUser] && usersMap[activity.actorUser].email"
                            />
                            <Avatar
                                v-if="activity.actor === 'chatbot'"
                                :size="32"
                                :username="'Chatbot'"
                                :src="baseUrl().concat('static/img/chatbot-bg.png')"
                            />
                            <Avatar
                                v-if="activity.actor === 'system'"
                                :size="32"
                                :username="'System'"
                                :src="baseUrl().concat('static/img/minilogo4.png')"
                            />
                        </div>
                        <div class="timeline-line"></div>
                    </div>
                    <div class="activity-body">
                        <div class="activity-header">
                            <span class="user-name" v-if="activity.actor === 'user'">
                                {{ (usersMap[activity.actorUser] && usersMap[activity.actorUser].name) || "Bilinmeyen Kullanıcı" }}
                            </span>
                            <span class="user-name" v-else-if="activity.actor === 'chatbot'">ChatBot</span>
                            <span class="user-name" v-else>Sistem</span>
                            <span class="activity-time">{{ $moment(activity.updatedAt).fromNow() }}</span>
                        </div>
                        <div class="activity-content" v-html="getActivityContent(activity)"></div>
                    </div>
                </div>
                <div v-if="hasMore" class="load-more-container">
                    <a-button :loading="loading" type="link" @click="loadMore">
                        Daha Fazla Yükle
                    </a-button>
                </div>
            </div>
            <div v-else class="empty-state">
                <a-icon type="file-text" class="empty-icon" />
                <p>Henüz bir hareket bulunmuyor.</p>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import Avatar from "vue-avatar";

export default {
    name: "ActivtyTask",
    components: {
        Avatar,
    },
    props: {
        taskId: {
            type: String,
            required: true,
        },
        loadOnMount: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            activities: null,
            page: 1,
            limit: 50,
            hasMore: false,
            loading: false,
        };
    },
    computed: {
        ...mapState({
            usersMap: function(state) {
                return state.user.users.data.reduce(function(acc, user) {
                    acc[user._id] = user;
                    return acc;
                }, {});
            },
            users: (state) =>
                state.user.users.data.reduce((acc, user) => {
                    acc.push({
                        value: user._id,
                        label: user.name,
                    });
                    return acc;
                }, []),
        }),
    },
    methods: {
        ...mapActions({
            getTaskActivities: "tasks/getTaskActivities",
        }),
        loadActivities() {
            if (!this.activities) {
                this.fetchActivities();
            }
        },
        async fetchActivities() {
            this.loading = true;
            try {
                const result = await this.getTaskActivities({
                    taskId: this.taskId,
                    page: this.page,
                    limit: this.limit,
                });
                const items = Array.isArray(result) ? result : result.data;
                const more = Array.isArray(result) ? false : result.hasMore;
                if (this.page === 1) {
                    this.activities = items;
                } else {
                    this.activities = this.activities.concat(items);
                }
                this.hasMore = more;
            } catch (error) {
                console.error("Error fetching activities:", error);
                if (this.page === 1) {
                    this.activities = [];
                }
            } finally {
                this.loading = false;
            }
        },
        loadMore() {
            this.page = this.page + 1;
            this.fetchActivities();
        },
        baseUrl: () => {
            if (document.currentScript) {
                let _documentUrl = document.currentScript.src.split("/");
                return _documentUrl[0] + "//" + _documentUrl[2] + "/";
            } else {
                return "/";
            }
        },
        getActivityContent(activity) {
            let user = this.usersMap[activity.actorUser]?.name || "Bilinmeyen Kullanıcı";
            if (activity.actor === "system") {
                user = "Sistem";
            } else if (activity.actor === "chatbot") {
                user = "ChatBot";
            }
            const task = activity.payload.name;
            const oldStage = activity.payload.oldStage;
            const newStage = activity.payload.newStage;
            const oldPriority = activity.payload.oldPriority;
            const newPriority = activity.payload.newPriority;
            const taskTitle = activity.payload.taskTitle || "Bilinmeyen Görev";
            const tags = activity.payload.tags?.join(", ");
            const ruleSource = activity.payload.ruleSource;
            const ruleInfo = ruleSource ? ` (<em>${ruleSource}</em> aracılığıyla)` : '';

            switch (activity.type) {
                case "task-created":
                    return this.$t("notification.taskCreated", { user, task }) + ruleInfo;
                case "task-stage-changed":
                    return this.$t("notification.taskStageChanged", { user, task, oldStage, newStage }) + ruleInfo;
                case "task-workflow-changed":
                    const oldWorkflowName = activity.payload.oldWorkflowName || "Bilinmeyen İş Akışı";
                    const newWorkflowName = activity.payload.newWorkflowName || "Bilinmeyen İş Akışı";
                    return `${user} kullanıcısı bu görevi "${oldWorkflowName}" iş akışından "${newWorkflowName}" iş akışına taşıdı${ruleInfo}.`;
                case "task-assigned-to-user":
                    return `<strong>${user}</strong> kullanıcısı, ${activity.payload.assignees.map(assignee => `<strong>${assignee}</strong>`).join(", ")} kullanıcısını <strong>"${taskTitle}"</strong> adlı göreve atadı${ruleInfo}.`;
                case "task-update-priority":
                    return this.$t("notification.taskPriorityUpdated", { user, oldPriority, newPriority, taskTitle }) + ruleInfo;
                case "task-added-tags":
                    return this.$t("notification.taskAddedTags", { user, taskTitle, tags }) + ruleInfo;
                case "task-removed-tags":
                    return `<strong>${user}</strong> kullanıcısı <strong>"${taskTitle}"</strong> adlı görevden şu etiketleri kaldırdı: <strong>${tags}</strong>${ruleInfo}.`;
                case "task-update-title":
                    return `<strong>${user}</strong> kullanıcısı görev başlığını <strong>"${activity.payload.oldTitle}"</strong> den <strong>"${activity.payload.newTitle}"</strong> olarak güncelledi${ruleInfo}.`;
                case "task-update-description":
                    return `<strong>${user}</strong> kullanıcısı <strong>"${taskTitle}"</strong> adlı görevin açıklamasını güncelledi${ruleInfo}.`;
                case "task-update-start-date":
                    return `<strong>${user}</strong> kullanıcısı <strong>"${taskTitle}"</strong> adlı görevin başlangıç tarihini <strong>"${activity.payload.oldStartDateFormatted}"</strong> den <strong>"${activity.payload.newStartDateFormatted}"</strong> olarak güncelledi${ruleInfo}.`;
                case "task-update-end-date":
                    return `<strong>${user}</strong> kullanıcısı <strong>"${taskTitle}"</strong> adlı görevin bitiş tarihini <strong>"${activity.payload.oldEndDateFormatted}"</strong> den <strong>"${activity.payload.newEndDateFormatted}"</strong> olarak güncelledi${ruleInfo}.`;
                case "task-update-owner":
                    return `<strong>${user}</strong> kullanıcısı <strong>"${taskTitle}"</strong> adlı görevin sahibini <strong>"${activity.payload.oldOwner}"</strong> den <strong>"${activity.payload.newOwner}"</strong> olarak değiştirdi${ruleInfo}.`;
                case "task-added-followers":
                    return `<strong>${user}</strong> kullanıcısı <strong>"${taskTitle}"</strong> adlı göreve şu takipçileri ekledi: <strong>${activity.payload.followers?.join(", ")}</strong>${ruleInfo}.`;
                case "task-removed-followers":
                    return `<strong>${user}</strong> kullanıcısı <strong>"${taskTitle}"</strong> adlı görevden şu takipçileri kaldırdı: <strong>${activity.payload.followers?.join(", ")}</strong>${ruleInfo}.`;
                case "task-added-collaborators":
                    return `<strong>${user}</strong> kullanıcısı <strong>"${taskTitle}"</strong> adlı göreve şu işbirlikçileri ekledi: <strong>${activity.payload.collaborators?.join(", ")}</strong>${ruleInfo}.`;
                case "task-update-checklist":
                    const addedItems = activity.payload.addedItems || [];
                    const removedItems = activity.payload.removedItems || [];
                    const completedItems = activity.payload.completedItems || [];
                    const uncompletedItems = activity.payload.uncompletedItems || [];

                    let checklistDetails = [];
                    if (addedItems.length > 0) checklistDetails.push(`Eklenen: ${addedItems.join(", ")}`);
                    if (removedItems.length > 0) checklistDetails.push(`Silinen: ${removedItems.join(", ")}`);
                    if (completedItems.length > 0) checklistDetails.push(`Tamamlanan: ${completedItems.join(", ")}`);
                    if (uncompletedItems.length > 0) checklistDetails.push(`Tamamlanmayan: ${uncompletedItems.join(", ")}`);

                    return `${user} kullanıcısı "${taskTitle}" adlı görevin kontrol listesini güncelledi. ${checklistDetails.join(". ")}${ruleInfo}`;
                case "task-added-subtask":
                    return `<strong>${user}</strong> kullanıcısı <strong>"${taskTitle}"</strong> adlı göreve <strong>${activity.payload.subtaskCount}</strong> alt görev ekledi: <strong>${activity.payload.subtasks?.join(", ")}</strong>${ruleInfo}.`;
                case "task-removed-subtask":
                    return `<strong>${user}</strong> kullanıcısı <strong>"${taskTitle}"</strong> adlı görevden <strong>${activity.payload.subtaskCount}</strong> alt görevi sildi: <strong>${activity.payload.subtasks?.join(", ")}</strong>${ruleInfo}.`;
                case "task-added-attachment":
                    return `${user} kullanıcısı "${taskTitle}" görevine ${activity.payload.attachmentCount} dosya ekledi: ${activity.payload.attachments?.join(", ")}${ruleInfo}.`;
                case "task-comment-added":
                    const commentPreview = activity.payload.comment ? activity.payload.comment.substring(0, 50) + (activity.payload.comment.length > 50 ? "..." : "") : "";
                    return `${user} kullanıcısı "${taskTitle}" görevine yorum ekledi: "${commentPreview}"${ruleInfo}`;
                case "task-comment-updated":
                    const oldCommentPreview = activity.payload.oldComment ? activity.payload.oldComment.substring(0, 30) + "..." : "";
                    const newCommentPreview = activity.payload.newComment ? activity.payload.newComment.substring(0, 30) + "..." : "";
                    return `${user} kullanıcısı "${taskTitle}" görevindeki yorumu düzenledi. Eski: "${oldCommentPreview}" → Yeni: "${newCommentPreview}"${ruleInfo}`;
                case "task-comment-deleted":
                    const deletedCommentPreview = activity.payload.comment ? activity.payload.comment.substring(0, 50) + (activity.payload.comment.length > 50 ? "..." : "") : "";
                    return `${user} kullanıcısı "${taskTitle}" görevinden yorum sildi: "${deletedCommentPreview}"${ruleInfo}`;
                case "task-completed":
                    return this.$t("notification.taskCompleted", { user, taskTitle }) + ruleInfo;
                case "task-archived":
                    return this.$t("notification.taskArchived", { user, taskTitle }) + ruleInfo;
                case "task-deleted":
                    return this.$t("notification.taskDeleted", { user, taskTitle }) + ruleInfo;
                case "task-restored":
                    return `${user} kullanıcısı "${taskTitle}" adlı görevi çöp kutusundan geri çıkardı${ruleInfo}.`;
                case "task-unarchived":
                    return `${user} kullanıcısı "${taskTitle}" adlı görevi arşivden geri çıkardı${ruleInfo}.`;
                default:
                    return `${activity.type} - ${taskTitle}`;
            }
        },
    },
};
</script>

<style lang="scss">
#ActivtyTask {
    .section-title {
        font-size: 0.8em;
        font-weight: 600;
        color: rgba(0, 0, 0, 0.4);
        text-transform: uppercase;
        letter-spacing: 0.08em;
        padding: 0 0 12px 0;
    }

    .activity-container {
        max-height: 560px;
        overflow-y: auto;
        padding: 4px 2px;

        &::-webkit-scrollbar {
            width: 4px;
        }
        &::-webkit-scrollbar-track {
            background: transparent;
        }
        &::-webkit-scrollbar-thumb {
            background: #e0e0e0;
            border-radius: 4px;
        }
    }

    .skeleton-list {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 8px 0;

        .skeleton-item {
            display: flex;
            align-items: flex-start;
            gap: 12px;

            .skeleton-avatar {
                width: 32px;
                height: 32px;
                border-radius: 50%;
                background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                background-size: 200% 100%;
                animation: shimmer 1.4s infinite;
                flex-shrink: 0;
            }

            .skeleton-body {
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 8px;
                padding-top: 4px;

                .skeleton-line {
                    height: 10px;
                    border-radius: 6px;
                    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                    background-size: 200% 100%;
                    animation: shimmer 1.4s infinite;

                    &.short {
                        width: 30%;
                    }
                    &.long {
                        width: 75%;
                    }
                }
            }
        }
    }

    .activity-list {
        display: flex;
        flex-direction: column;

        .activity-item {
            display: flex;
            gap: 12px;
            position: relative;

            &:last-child .timeline-line {
                display: none;
            }

            .activity-left {
                display: flex;
                flex-direction: column;
                align-items: center;
                flex-shrink: 0;
                width: 32px;

                .avatar-wrapper {
                    z-index: 1;
                }

                .timeline-line {
                    width: 2px;
                    flex: 1;
                    min-height: 16px;
                    background: #f0f0f0;
                    margin-top: 6px;
                }
            }

            .activity-body {
                flex: 1;
                padding-bottom: 20px;

                .activity-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 4px;

                    .user-name {
                        font-weight: 600;
                        font-size: 0.85em;
                        color: rgba(0, 0, 0, 0.8);
                    }

                    .activity-time {
                        font-size: 0.75em;
                        color: rgba(0, 0, 0, 0.35);
                        white-space: nowrap;
                    }
                }

                .activity-content {
                    font-size: 0.85em;
                    color: rgba(0, 0, 0, 0.6);
                    line-height: 1.5;
                    background: #fafafa;
                    border: 1px solid #f0f0f0;
                    border-radius: 8px;
                    padding: 8px 12px;
                }
            }
        }

        .load-more-container {
            display: flex;
            justify-content: center;
            padding: 4px 0 8px;
        }
    }

    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 40px 20px;
        color: rgba(0, 0, 0, 0.3);

        .empty-icon {
            font-size: 32px;
            margin-bottom: 10px;
            color: #d9d9d9;
        }

        p {
            margin: 0;
            font-size: 0.9em;
        }
    }
}

@keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
}
</style>
